import { writable, derived } from "svelte/store";
import { api } from "#src/api";

import { storedWritable } from "./ui.js";
import { alerts } from "./alerts.js";
import { storeJob } from "./jobs.js";
import createRecordList from "./record-list.js";
import {
  selected,
  sortList,
} from "@local/extensions/collections/sortable-list.js";
import { profile } from "./auth.js";
import { myRfqsOnly } from "./ui.js";

let $profile;
profile.subscribe((value) => {
  $profile = value;
});

const ls = JSON.parse(localStorage.getItem("review-jobs")) || {};
const stored = {
  selected_review_job_ids: [],
  ...ls,
};
localStorage.setItem("review-jobs", JSON.stringify(stored));

function createJobs() {
  const { subscribe, load, loadOne, insert, unload, sort, updateOne } =
    createRecordList();

  let $jobs;
  subscribe((value) => {
    $jobs = value;
  });

  const jobUpdate = async (payload) => {
    if (payload.eventType === "DELETE") {
      if ($jobs[payload.old.id]) unload(payload.old.id);
    } else if (payload.eventType === "UPDATE") {
      if ($jobs[payload.new.id]) {
        if (payload.new.archived_at) {
          unload(payload.new.id);
        } else {
          const { data } = await api
            .from("job_review_view")
            .select("*")
            .eq("id", payload.new.id)
            .eq("organization_id", $profile.organization_id)
            .single();

          if (data) loadOne(data);
        }
      } else if (
        payload.new.organization_id === $profile.organization_id &&
        !payload.new.archived_at
      ) {
        const { data } = await api
          .from("job_review_view")
          .select("*")
          .eq("id", payload.new.id)
          .eq("organization_id", $profile.organization_id)
          .single();

        if (data && !data.archived_at) loadOne(data);
      }
    } else if (
      payload.eventType === "INSERT" &&
      payload.new.organization_id === $profile.organization_id
    ) {
      const { data } = await api
        .from("job_review_view")
        .select("*")
        .eq("id", payload.new.id)
        .eq("organization_id", $profile.organization_id)
        .single();

      if (data) loadOne(data);
    }
  };

  let rfqSubscription;

  const notificationUpdate = async (payload) => {
    if ($jobs[payload.new.quote_request_id]) {
      const { data } = await api
        .from("job_review_view")
        .select("*")
        .eq("id", payload.new.quote_request_id)
        .eq("organization_id", $profile.organization_id)
        .single();

      if (data) loadOne(data);
    }
  };

  let notificationSubscription;

  return {
    subscribe,
    load,
    loadOne,
    unload,
    sort,

    // Subscribe to DB changes
    async subscribeToDbUpdates() {
      await this.unsubscribe();

      rfqSubscription = api
        .channel("review_job-changes")
        .on(
          "postgres_changes",
          {
            event: "DELETE",
            schema: "public",
            table: "quote_requests",
            filter: `organization_id=eq.${$profile.organization_id}`,
          },
          jobUpdate,
        )
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "quote_requests",
            filter: `organization_id=eq.${$profile.organization_id}`,
          },
          jobUpdate,
        )
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "quote_requests",
            filter: `organization_id=eq.${$profile.organization_id}`,
          },
          jobUpdate,
        )
        .subscribe();

      notificationSubscription = api
        .channel("review_job-notification-changes")
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: `organization_id=eq.${$profile.organization_id}`,
          },
          notificationUpdate,
        )
        .subscribe();
    },

    async unsubscribe() {
      if (rfqSubscription) {
        await rfqSubscription.unsubscribe();
      }

      if (notificationSubscription) {
        await notificationSubscription.unsubscribe();
      }
    },

    // Retrieves records from DB
    async fetch() {
      try {
        let query = api
          .from("job_review_view")
          .select("*,shpx_requested_pages(*)")
          .eq("organization_id", $profile.organization_id)
          .is("archived_at", null)
          .order("created_at", { ascending: false });

        const { data, error } = await query;

        if (error) throw error;
        load(data);
      } catch (error) {
        console.log(error);
      }
    },

    async fetchById(...ids) {
      try {
        const { data, error } = await api
          .from("job_review_view")
          .select("*")
          .in("id", ids);

        if (error) throw error;
        insert(data);
      } catch (error) {
        console.log(error);
      }
    },

    async fetchOne(id, sortPosition) {
      try {
        const { data, error } = await api
          .from("job_review_view")
          .select("*")
          .eq("id", id)
          .single();
        if (error) throw error;

        loadOne(data, sortPosition);
      } catch (error) {
        console.log(error);
      }
    },

    async add(job, sortPosition, org) {
      try {
        const settings = org?.default_group_settings || {};
        const j = await storeJob(job, "unconstrained", settings);
        await this.fetchOne(j.id, sortPosition);
      } catch (error) {
        console.log(error);
      }
    },

    // Returns whether the job actually went away. Callers may ignore it, but a
    // false return means the card must NOT be unloaded.
    async remove(id) {
      try {
        // .select() makes the delete return the rows it removed. Without it a
        // zero-row delete — the shape row-level security produces — is
        // indistinguishable from a successful one, so the card would vanish
        // while the job stayed in the database.
        const { data, error } = await api.from("jobs").delete().eq("id", id).select("id");

        if (error) throw error;
        if (!data?.length) throw new Error(`Job ${id} was not deleted: no matching row.`);
        unload(id);
        return true;
      } catch (error) {
        alerts.add({ type: "danger", message: "Could not delete this job." });
        console.log(error);
        return false;
      }
    },

    async archive(...ids) {
      try {
        const { error } = await api
          .from("jobs")
          .update({ archived_at: new Date() })
          .in("id", ids);

        if (error) throw error;

        unload(...ids);
      } catch (error) {
        console.log(error);
      }
    },

    async unarchive(id) {
      try {
        const j = await api
          .from("jobs")
          .update({ archived_at: null })
          .eq("id", id);

        if (j.error) throw j.error;

        const jl = await api
          .from("job_list_view")
          .select("*")
          .eq("id", id)
          .single();

        if (jl.error) throw jl.error;

        loadOne(jl.data);
      } catch (error) {
        console.log(error);
      }
    },

    async spin(id) {
      try {
        updateOne(id, { spinning: true });
      } catch (error) {
        console.log(error);
      }
    },
  };
}

export const jobs = createJobs();
export const jobFilter = writable("");
export const jobSortBy = writable("sent_at");
export const jobSortDirection = writable("desc");

function sortCategory(job) {
  if (job.status === "canceled") return 2;
  if (job.sent_at) return 3;
  return 1;
}

function mostRecent(quotes) {
  return quotes.reduce((r, quote) => {
    if (!r) return quote;
    return new Date(quote.sent_at) > new Date(r.sent_at) ? quote : r;
  }, null);
}

export const filteredJobs = derived(
  [jobs, jobFilter, jobSortBy, jobSortDirection, myRfqsOnly, profile],
  ([$list, $filter, $sortBy, $sortDirection, $mineOnly, $profile]) => {
    const ids = $list?.order.filter((id) => {
      const job = $list[id];
      if (
        $mineOnly &&
        job.assigned_to_id &&
        job.assigned_to_id !== $profile.id
      ) {
        return false;
      }
      const str = $filter.toLowerCase();
      const name = (job.name || "").toLowerCase();
      const location = (job.location || "").toLowerCase();
      const seller_reference = (job.seller_reference || "").toLowerCase();
      if (name.includes(str)) return true;
      if (location.includes(str)) return true;
      if (seller_reference.includes(str)) return true;
      return false;
    });
    const f = selected($list, ids);

    sortList(f, {
      sortFunction: (a, b) => {
        const aCat = sortCategory(a);
        const bCat = sortCategory(b);

        if (aCat !== bCat) return aCat - bCat;

        if (aCat === 1 && bCat === 1) {
          return new Date(b.created_at) - new Date(a.created_at);
        }

        const aQuote = mostRecent((a.quotes || []).filter((q) => q.sent_at));
        const bQuote = mostRecent((b.quotes || []).filter((q) => q.sent_at));

        if (aQuote && bQuote)
          return new Date(bQuote.sent_at) - new Date(aQuote.sent_at);
        if (aQuote) return -1;
        if (bQuote) return 1;

        return new Date(b.sent_at) - new Date(a.sent_at);
      },
    });
    return f;
  },
);

export function jobArray(filteredJobs) {
  return derived(filteredJobs, ($list) => $list.order.map((id) => $list[id]));
}

export const selectedJobIds = storedWritable(
  "selected_review_job_ids",
  stored.selected_review_job_ids,
  "jobs",
);
